import { dumpLogs } from "@modules/home/hooks/dumpLogs";
import { FigmaJsonStates } from "@modules/home/view/activity/Components/Blocks/AllInOne/FigmaStates";
import { captureException } from "@sentry/nextjs";
import {
    createAudioKit,
    type AudioKit,
    type PlayResult,
    type SoundItem,
} from "./audioKit";

class Sound {
    private audioKit!: AudioKit;
    private sounds: Record<
        string,
        {
            path: string;
            url: string;
            blob?: string;
            loaded: boolean;
            audio?: HTMLAudioElement | null;
            props?: any;
            duration: number | null;
        }
    > = {};
    showPopup = () => {};
    removePopup = () => {};
    onError = (_e: any) => {};
    pauseSound = false;
    questionAudios: any[] = [];
    private pausedAudioId: string | null = null;
    private lastPlayRequest: { name: string; timestamp: number } | null = null;
    private activePlayPromise: Promise<PlayResult> | null = null; // Track active play
    private pendingPlayQueue: Array<{ name: string; resolve: () => void }> = []; // Queue interrupted plays
    private activePlays: Set<string> = new Set(); // Track concurrent plays

    // Service Worker preloading properties
    private preloadQueue: Array<{ id: string; url: string; props?: any }> = [];
    private isProcessingQueue = false;
    private pendingPreloads = new Map<
        string,
        {
            resolve: Function;
            reject: Function;
            props?: any;
            timeout?: NodeJS.Timeout;
        }
    >();
    private swAvailable = false;

    constructor() {
        // Only initialize in browser environment
        if (typeof window !== "undefined") {
            this.initializeAudioKit();
            this.handleAudioInterruption();
            this.setupServiceWorkerListener();
        } else {
            // Create a mock audioKit for SSR
            this.audioKit = createAudioKit({});
        }
    }

    private initializeAudioKit() {
        this.audioKit = createAudioKit({
            playMode: "mono",
            cacheName: "mathai-audio-v1",
            logger: {
                debug: (msg, ctx) =>
                    dumpLogs({ action: "audio_debug", msg, ...ctx }),
                info: (msg, ctx) =>
                    dumpLogs({ action: "audio_info", msg, ...ctx }),
                warn: (msg, ctx) =>
                    dumpLogs({ action: "audio_warn", msg, ...ctx }),
                error: (msg, ctx) =>
                    dumpLogs({ action: "audio_error", msg, ...ctx }),
            },
            telemetry: {
                event: (name, props) =>
                    dumpLogs({
                        action: name,
                        pauseSound: this.pauseSound,
                        ...props,
                    }),
            },
            sentry: {
                captureException: (e, ctx) => captureException(e, ctx),
            },
            retry: {
                retries: 2, // Reduced from 3
                baseDelayMs: 300,
                maxDelayMs: 3000, // Reduced from 5000ms
                jitter: true,
                timeoutMs: 5000, // Reduced from 12000ms to 5s
            },
            cooldown: {
                globalMinIntervalMs: 80,
                perIdMinIntervalMs: {},
                onDrop: (id) => {
                    dumpLogs({
                        action: "audio_dropped_cooldown",
                        sound_name: id,
                        pauseSound: this.pauseSound,
                    });
                },
            },
            memory: {
                maxDecodedBytes: 32 * 1024 * 1024,
                maxBuffers: 256,
            },
            transform: {
                downmixToMono: true, // Optimize for mobile
            },
            preempt: {
                minTailMs: 0, // No tail protection - always interrupt
                dropOnTail: false, // Never drop new audio, always play it
            },
        });
    }

    private setupServiceWorkerListener() {
        // Check if noCache=true or SW is disabled
        const isNoCacheMode =
            window?.location?.search?.includes("noCache=true");
        const swDisabled =
            String(process.env.NEXT_PUBLIC_REMOVE_SERVICE_WORKER) === "true";

        if (isNoCacheMode || swDisabled) {
            this.swAvailable = false;
            return;
        }

        // Check if workbox is available with max retry limit
        let retryCount = 0;
        const maxRetries = 10; // Max 10 seconds of retrying

        const checkWorkbox = () => {
            // Check for both workbox and native service worker
            const workbox = (window as any).workbox;
            const swReady =
                navigator.serviceWorker?.controller || workbox?.current;

            if (swReady) {
                this.swAvailable = true;
            } else if (retryCount < maxRetries) {
                retryCount++;

                setTimeout(checkWorkbox, 1000);
            } else {
                this.swAvailable = false;
            }
        };
        checkWorkbox();

        // Listen for messages from service worker
        navigator.serviceWorker?.addEventListener("message", async (event) => {
            if (event.data.type === "AUDIO_CACHED") {
                const { id, success, error } = event.data;

                if (this.sounds[id]) {
                    const soundObj = this.sounds[id];
                    if (success) {
                        // SW has cached the file, now we need audioKit to decode it for duration

                        try {
                            // Load the audio into audioKit to decode and get duration
                            if (!this.audioKit.has(id)) {
                                await this.audioKit.preloadCritical([
                                    { id: id, url: this.sounds[id].url },
                                ]);
                            }
                            if (!this.sounds?.[id]) {
                                this.sounds[id] = soundObj ?? {};
                            }

                            // Now we should have the actual duration
                            const duration = this.audioKit.getDuration(id) || 5;
                            this.sounds[id].duration = duration;
                            this.sounds[id].loaded = true;

                            // Call callbacks with actual duration

                            this.sounds[id].props?.setDuration?.(duration);

                            this.sounds[id].props?.loaded?.();
                        } catch (decodeError) {
                            this.sounds[id].loaded = true; // Still mark as loaded since it's cached

                            this.sounds[id].props?.setDuration?.(5); // Use default duration

                            this.sounds[id].props?.loaded?.();
                        }
                    } else {
                        // Even on error, we need to call callbacks with defaults
                        this.sounds[id].loaded = true; // Mark as "loaded" even if failed
                        this.sounds[id].duration = 5; // Default duration

                        this.sounds[id].props?.setDuration?.(5);

                        this.sounds[id].props?.loaded?.(); // IMPORTANT: Call loaded callback even on error
                    }
                }

                // Resolve the promise for this audio
                if (this.pendingPreloads.has(id)) {
                    const pending = this.pendingPreloads.get(id);
                    if (pending) {
                        if (pending.timeout) clearTimeout(pending.timeout);
                        pending.resolve();
                        this.pendingPreloads.delete(id);
                    }
                }
            }
        });
    }

    private handleAudioInterruption() {
        if (typeof window == "undefined") return;

        // For iOS - check both platform and userAgent for maximum compatibility
        const isIOS = (() => {
            // Check userAgent first (more reliable on newer devices)
            if (navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) return true;

            // Fallback to platform for older devices (despite deprecation)
            try {
                if (
                    navigator.platform &&
                    navigator.platform.match(/(iPhone|iPod|iPad)/i)
                )
                    return true;
            } catch (e) {
                // Ignore if platform is not available
            }

            // Additional check for iPadOS 13+ requesting desktop site
            if (
                navigator.platform === "MacIntel" &&
                navigator.maxTouchPoints > 1
            )
                return true;

            return false;
        })();

        if (isIOS) {
            document.addEventListener("visibilitychange", () => {
                if (document.hidden) {
                    dumpLogs({
                        action: "sound_pause_visibility_change",
                        pauseSound: this.pauseSound,
                    });
                    this.pause();
                } else {
                    // Optionally resume playback when the app comes back to foreground
                    // this.resume();
                }
            });

            window.addEventListener("pagehide", () => {
                dumpLogs({
                    action: "sound_pause_page_hide",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });

            // Periodic check for unexpected pauses
            setInterval(() => {
                const audioElement = document.getElementById(
                    "interactionSoundComponent",
                ) as HTMLAudioElement;

                if (audioElement && !audioElement.paused && document.hidden) {
                    dumpLogs({
                        action: "sound_pause_periodic",
                        pauseSound: this.pauseSound,
                    });
                    this.pause();
                }
            }, 1000); // Check every second
        }

        // For Android - check both userAgent and platform for compatibility
        const isAndroid = (() => {
            // Primary check via userAgent
            if (/Android/i.test(navigator.userAgent)) return true;

            // Fallback to platform for some older Android devices
            try {
                if (
                    navigator.platform &&
                    /Linux/i.test(navigator.platform) &&
                    /Android/i.test(navigator.userAgent)
                )
                    return true;
            } catch (e) {
                // Ignore if platform is not available
            }

            return false;
        })();

        if (isAndroid) {
            window.addEventListener("blur", () => {
                dumpLogs({
                    action: "sound_pause_blur",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });

            // Optional: resume playback when focus is regained
            window.addEventListener("focus", () => {
                // this.resume();
            });
        }

        // General approach for modern browsers
        if ("mediaSession" in navigator) {
            navigator.mediaSession.setActionHandler("pause", () => {
                dumpLogs({
                    action: "sound_pause_action",
                    pauseSound: this.pauseSound,
                });
                this.pause();
            });
        }

        // Handle audio context state changes
        try {
            if (window?.AudioContext || (window as any)?.webkitAudioContext) {
                const audioContext = new (window?.AudioContext ||
                    (window as any)?.webkitAudioContext)();
                audioContext.onstatechange = () => {
                    if (audioContext.state === "interrupted") {
                        dumpLogs({
                            action: "sound_pause_interrupt",
                            pauseSound: this.pauseSound,
                        });
                        this.pause();
                    } else if (audioContext?.state === "running") {
                        // Optionally resume playback
                        // this.resume();
                    }
                };
            }
        } catch (e) {
            captureException(e);
        }
    }
    resetSrc = (audioElement: HTMLAudioElement) => {
        // Kept for backward compatibility but no longer used with audioKit
        if (!audioElement) return;
        const events = [
            "onerror",
            "onabort",
            "oncancel",
            "onclose",
            "onstalled",
            "onloadstart",
            "ondurationchange",
            "onloadeddata",
            "onprogress",
            "oncanplay",
            "oncanplaythrough",
            "onloadedmetadata",
            "onended",
            "onpause",
            "onplay",
        ];
        for (const event of events) {
            (audioElement as any)[event] = null;
        }
        audioElement.src = "";
    };

    private transformUrlForCDN(url: string): string {
        if (!url) return url;

        // Pattern 1: Transform cdn.homeworkapp.ai/sets-gamify-assets to cdn.mathai.ai/sets-gamify-assets
        // if (url.includes("cdn.homeworkapp.ai/sets-gamify-assets")) {
        //     return url.replace(
        //         "cdn.homeworkapp.ai/sets-gamify-assets",
        //         "cdn.mathai.ai/sets-gamify-assets",
        //     );
        // }

        // Pattern 2: Transform sets-gamify-assets.s3.ap-south-1.amazonaws.com to cdn.mathai.ai/sets-gamify-assets
        if (url.includes("sets-gamify-assets.s3.ap-south-1.amazonaws.com")) {
            return url.replace(
                "sets-gamify-assets.s3.ap-south-1.amazonaws.com",
                "cdn.mathai.ai/sets-gamify-assets",
            );
        }

        // Return original URL if no transformation needed
        return url;
    }

    private async processPreloadQueue() {
        if (this.isProcessingQueue || !this.swAvailable) {
            return;
        }

        this.isProcessingQueue = true;

        const BATCH_SIZE = 3; // Process 3 audios at a time
        const BATCH_DELAY = 500; // 500ms between batches

        while (this.preloadQueue.length > 0) {
            const batch = this.preloadQueue.splice(0, BATCH_SIZE);

            try {
                await this.sendBatchToServiceWorker(batch);
            } catch (error) {
                // Fallback to audioKit for this batch
                for (const item of batch) {
                    await this.fallbackToAudioKit(item);
                }
            }

            // Wait before processing next batch
            if (this.preloadQueue.length > 0) {
                await new Promise((resolve) =>
                    setTimeout(resolve, BATCH_DELAY),
                );
            }
        }

        this.isProcessingQueue = false;
    }

    private async sendBatchToServiceWorker(
        batch: Array<{ id: string; url: string; props?: any }>,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const workbox = (window as any).workbox;
            const hasWorkbox = workbox?.current;
            const hasController = navigator.serviceWorker?.controller;

            if (!hasWorkbox && !hasController) {
                reject(new Error("No service worker available"));
                return;
            }

            let completed = 0;
            const total = batch.length;

            // Track promises with timeout
            batch.forEach((item) => {
                const timeout = setTimeout(() => {
                    if (this.pendingPreloads.has(item.id)) {
                        // On timeout, ensure callbacks are called with defaults
                        if (this.sounds[item.id]) {
                            this.sounds[item.id].loaded = true; // Mark as loaded even on timeout
                            this.sounds[item.id].duration = 5; // Default duration

                            this.sounds[item.id].props?.setDuration?.(5);

                            this.sounds[item.id].props?.loaded?.();
                        }

                        this.pendingPreloads.get(item.id)?.resolve();
                        this.pendingPreloads.delete(item.id);
                        completed++;
                        if (completed === total) resolve();
                    }
                }, 5000); // 5 second timeout (reduced from 30s)

                this.pendingPreloads.set(item.id, {
                    resolve: () => {
                        clearTimeout(timeout);
                        completed++;
                        if (completed === total) resolve();
                    },
                    reject: (err: any) => {
                        clearTimeout(timeout);
                        completed++;
                        if (completed === total) resolve(); // Still resolve batch even if one item fails
                    },
                    props: item.props,
                    timeout,
                });
            });

            const message = {
                type: "AUDIO_PRELOAD",
                payload: batch.map((b) => ({
                    id: b.id,
                    url: b.url,
                })),
            };

            // Try workbox first, then direct postMessage
            if (hasWorkbox) {
                workbox.current.messageSW(message).catch((error: any) => {
                    // Try direct postMessage as fallback
                    if (hasController) {
                        navigator.serviceWorker.controller!.postMessage(
                            message,
                        );
                    } else {
                        reject(error);
                    }
                });
            } else if (hasController) {
                // Use direct postMessage to service worker
                navigator.serviceWorker.controller!.postMessage(message);
            }
        });
    }

    private async fallbackToAudioKit(item: {
        id: string;
        url: string;
        props?: any;
    }) {
        try {
            // Use preloadCritical for immediate loading to get duration
            await this.audioKit.preloadCritical([
                { id: item.id, url: item.url },
            ]);

            // Get actual duration after loading
            const duration = this.audioKit.getDuration(item.id) || 5;

            if (this.sounds[item.id]) {
                this.sounds[item.id].loaded = true;
                this.sounds[item.id].duration = duration;

                this.sounds[item.id].props?.setDuration?.(duration);

                this.sounds[item.id].props?.loaded?.();
            }
        } catch (error) {
            // Set default duration on error and ensure callbacks are called
            if (this.sounds[item.id]) {
                this.sounds[item.id].loaded = true; // Mark as loaded even on error
                this.sounds[item.id].duration = 5; // Default duration

                this.sounds[item.id].props?.setDuration?.(5);

                this.sounds[item.id].props?.loaded?.(); // IMPORTANT: Call loaded callback even on error
            }
        }
    }

    preloadAudio(name: string, url: string, props?: any) {
        // Transform URL to avoid CORS issues
        const transformedUrl = this.transformUrlForCDN(url);

        dumpLogs({
            action: "preload_audio_queue",
            sound_name: name,
            sound_url: transformedUrl,
            original_url: url !== transformedUrl ? url : undefined,
            sw_available: this.swAvailable,
            pauseSound: this.pauseSound,
        });

        // Initialize sound metadata
        if (!this.sounds[name]) {
            this.sounds[name] = {
                path: transformedUrl,
                url: transformedUrl,
                loaded: false,
                audio: null,
                duration: 5,
                props,
            };
        } else {
            // Update existing entry
            this.sounds[name].url = transformedUrl;
            this.sounds[name].path = transformedUrl;
            // Preserve existing props if not provided, but merge in new ones
            this.sounds[name].props = props || this.sounds[name].props;
        }

        // Set initial duration immediately

        props?.setDuration?.(this.sounds[name].duration || 5);

        // Check if SW is available
        if (!this.swAvailable) {
            this.fallbackToAudioKit({ id: name, url: transformedUrl, props });
            return;
        }

        // Check if already in queue
        const alreadyQueued = this.preloadQueue.some(
            (item) => item.id === name,
        );
        if (alreadyQueued) {
            return;
        }

        // Add to queue for SW processing
        this.preloadQueue.push({
            id: name,
            url: transformedUrl,
            props,
        });

        // Start processing queue
        this.processPreloadQueue();
    }
    add(name: string, path: string, props?: any) {
        if (this.sounds[name] && this.sounds[name].path == path) {
            // Update props even if path is same
            if (props) {
                this.sounds[name].props = props;
            }

            props?.setDuration &&
                props?.setDuration?.(this.sounds[name].duration || 5);
            return;
        }
        this.sounds[name] = {
            path,
            url: path,
            loaded: false,
            audio: null,
            duration: 5, // Set default duration
            props,
        };
        this.preloadAudio(name, path, props);
    }
    pauseIfPopupIsEnabled() {
        // pause if popup-layout is visible
        const div = document.getElementById("popup-layout");
        if (div) {
            dumpLogs({
                action: "audio_pause_if_popup_is_enabled",
                pauseSound: this.pauseSound,
            });
            this.pause();
        }
    }
    getSoundName(audioElement: HTMLAudioElement): string {
        const url = decodeURIComponent(audioElement.src);
        const isBlob = url.includes("blob");
        return (
            Object.keys(this.sounds).find(
                (i) => this.sounds[i]?.[isBlob ? "blob" : "url"] == url,
            ) ?? ""
        );
    }

    async play(
        name: string,
        callbacks: {
            interaction?: boolean;
            complete?: () => void;
            onPlay?: () => void;
            error?: (arg0: String, ...rest: any) => void;
            onSkip?: () => void;
            priority?: number; // Sound priority for preemption
            triggerSource?: string; // Source that triggered the sound
        } = {},
    ) {
        const playStartTime = Date.now();
        const callStack = new Error().stack;

        this.activePlays.add(name);

        // Debounce protection: if the same audio is already playing, skip
        const currentlyPlaying = this.audioKit.getCurrentlyPlaying();
        if (currentlyPlaying === name) {
            this.activePlays.delete(name);
            callbacks.complete?.(); // Ensure flow continues even when skipping duplicate
            return;
        }

        // Time-based debounce: prevent same audio from being triggered within 50ms
        const now = Date.now();
        if (
            this.lastPlayRequest &&
            this.lastPlayRequest.name === name &&
            now - this.lastPlayRequest.timestamp < 50
        ) {
            this.activePlays.delete(name);
            callbacks.complete?.(); // Ensure flow continues even when skipping rapid duplicate
            return;
        }

        dumpLogs({
            action: "play_audio_start",
            sound_name: name,
            sound: this.sounds[name],
            pauseSound: this.pauseSound,
            timestamp: playStartTime,
            duration: this.sounds[name]?.duration,
        });

        // Check if paused - Wait for resume before continuing
        if (this.pauseSound) {
            this.showPopup();

            // Store the audio name to play after resume
            this.pausedAudioId = name;

            // Wait for resume to be called - use queue to handle multiple blocked plays
            await new Promise<void>((resolve) => {
                this.pendingPlayQueue.push({ name, resolve });

                // Safety timeout - don't wait forever
                setTimeout(() => {
                    const index = this.pendingPlayQueue.findIndex(
                        (item) => item.resolve === resolve,
                    );
                    if (index >= 0) {
                        this.pendingPlayQueue.splice(index, 1);
                        resolve();
                    }
                }, 10000); // 10 second safety timeout
            });

            // After resume, continue with the play
        }

        // Just-in-time loading: If sound metadata exists but audio not loaded, load it now
        // This serves as a fallback if preloading hasn't completed yet
        if (this.sounds[name] && !this.audioKit.has(name)) {
            const soundObj = this.sounds[name];
            try {
                await this.audioKit.preloadCritical([
                    { id: name, url: this.sounds[name].url },
                ]);

                // Update duration after loading
                const duration = this.audioKit.getDuration(name) || 5;
                if (!this.sounds[name]) {
                    this.sounds[name] = soundObj;
                }
                if (this.sounds[name]) {
                    this.sounds[name].duration = duration;
                    this.sounds[name].loaded = true;
                    // Update UI with actual duration

                    this.sounds[name].props?.setDuration?.(duration);

                    this.sounds[name].props?.loaded?.(); // IMPORTANT: Call loaded callback
                }
            } catch (e) {
                // Set default duration on error
                if (this.sounds[name]) {
                    this.sounds[name].props?.setDuration?.(5);

                    this.sounds[name].props?.loaded?.(); // IMPORTANT: Call loaded callback even on error
                }
                callbacks.error?.("JIT_LOAD_FAILED");
                this.activePlays.delete(name);
                callbacks.complete?.();
                return;
            }
        }

        // Check if sound exists
        if (!this.sounds[name] && !this.audioKit.has(name)) {
            dumpLogs({
                action: "play_audio_not_found",
                sound_name: name,
                sound: this.sounds,
                pauseSound: this.pauseSound,
            });
            callbacks.error?.("NOT_FOUND");
            this.activePlays.delete(name);
            callbacks.complete?.(); // Ensure flow continues even on error
            return;
        }

        try {
            // Handle popup if needed
            this.pauseIfPopupIsEnabled();

            // Remove popup on play start
            if (callbacks.interaction) {
                this.removePopup();
            }

            callbacks.onPlay?.();

            // Track the active play promise
            const beforePlayAndWait = Date.now();

            // Create the play promise
            const playPromise = this.audioKit.playAndWait(name, {
                volume: 1,
                priority: callbacks.priority ?? 0, // Default priority 0
                triggerSource: callbacks.triggerSource ?? "unknown",
                // maxMs not specified - will use audio duration + 1.5s guard
            });

            // Store it as active
            this.activePlayPromise = playPromise;

            // Check if pause happened while we were setting up
            if (this.pauseSound) {
                // Store this for resume
                this.pausedAudioId = name;

                // Wait for resume before continuing
                await new Promise<void>((resolve) => {
                    this.pendingPlayQueue.push({ name, resolve });

                    // Safety timeout - don't wait forever
                    setTimeout(() => {
                        const index = this.pendingPlayQueue.findIndex(
                            (item) => item.resolve === resolve,
                        );
                        if (index >= 0) {
                            this.pendingPlayQueue.splice(index, 1);
                            resolve();
                        }
                    }, 10000); // 10 second safety timeout
                });
            }

            const result = await playPromise;

            this.activePlayPromise = null; // Clear after completion

            const afterPlayAndWait = Date.now();
            const playAndWaitDuration = afterPlayAndWait - beforePlayAndWait;
            const totalDuration = afterPlayAndWait - playStartTime;

            dumpLogs({
                action: `play_audio_${result.status}`,
                sound_name: name,
                sound: this.sounds[name],
                pauseSound: this.pauseSound,
                result,
            });

            switch (result.status) {
                case "ok":
                    if (callbacks.complete) {
                        callbacks.complete();
                    }
                    this.activePlays.delete(name);

                    break;
                case "timeout":
                    callbacks.error?.("TIMEOUT");
                    this.onError?.({ msg: "TIMEOUT", id: name });
                    if (callbacks.complete) {
                        callbacks.complete();
                    }
                    this.activePlays.delete(name);
                    break;
                case "missing":
                    callbacks.error?.("NOT_FOUND");
                    if (callbacks.complete) {
                        callbacks.complete();
                    }
                    this.activePlays.delete(name);
                    break;
                case "error":
                    if (
                        String(result.error).includes(
                            "interact with the document",
                        )
                    ) {
                        callbacks.error?.("NO_TAP");
                        this.onError?.("NO_TAP");
                    } else {
                        callbacks.error?.("PLAY_ERROR", result.error);
                        this.onError?.({
                            msg: "PLAY_ERROR",
                            error: result.error,
                        });
                    }
                    if (callbacks.complete) {
                        callbacks.complete();
                    }
                    this.activePlays.delete(name);
                    break;
                case "preempted":
                case "dropped":
                    callbacks.onSkip?.();
                    if (callbacks.complete) {
                        callbacks.complete();
                    }
                    this.activePlays.delete(name);

                    break;
            }
        } catch (e) {
            dumpLogs({
                action: "play_audio_error",
                sound_name: name,
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
            callbacks.error?.("FAILED", e);
            this.activePlays.delete(name);
            callbacks.complete?.(); // Ensure flow continues even on exception
        }
    }

    async playDirect(
        url: string,
        callbacks: {
            interaction?: boolean;
            complete?: () => void;
            onPlay?: () => void;
            error?: (arg0: String, ...rest: any) => void;
            onSkip?: () => void;
            priority?: number; // Sound priority for preemption
            triggerSource?: string; // Source that triggered the sound
        },
    ) {
        if (!url) {
            callbacks.error?.("ERROR");
            callbacks.complete?.(); // Ensure flow continues even on error
            return;
        }

        // Block tap audio exception - these should use regular play()
        if (url.includes("sets-gamify-assets/dev/worksheet/audio/")) {
            callbacks.onSkip?.();
            callbacks.complete?.();
            return;
        }

        // Transform URL to avoid CORS issues
        const transformedUrl = this.transformUrlForCDN(url);

        // Generate a unique ID for this direct play
        const directId = `direct_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

        dumpLogs({
            action: "direct_play_audio_start",
            sound_url: transformedUrl,
            original_url: url !== transformedUrl ? url : undefined,
            pauseSound: this.pauseSound,
        });

        try {
            // Preload the URL first
            await this.audioKit.preloadCritical([
                { id: directId, url: transformedUrl },
            ]);

            // Then play it
            callbacks.onPlay?.();
            if (callbacks.interaction) {
                this.removePopup();
            }

            const result = await this.audioKit.playAndWait(directId, {
                volume: 1,
                priority: callbacks.priority ?? 0, // Default priority 0
                triggerSource: callbacks.triggerSource ?? "unknown",
                // maxMs not specified - will use audio duration + 1.5s guard
            });

            dumpLogs({
                action: `direct_play_audio_${result.status}`,
                sound_url: url,
                pauseSound: this.pauseSound,
                result,
            });

            if (result.status === "ok") {
                callbacks.complete?.();
            } else if (result.status === "error") {
                if (
                    String(result.error).includes("interact with the document")
                ) {
                    callbacks.error?.("NO_TAP");
                    this.onError?.("NO_TAP");
                } else {
                    callbacks.error?.("ERROR", result.error);
                }
                callbacks.complete?.(); // Ensure flow continues even on error
            } else if (result.status === "timeout") {
                callbacks.error?.("TIMEOUT");
                callbacks.complete?.(); // Ensure flow continues even on timeout
            } else {
                // Handle any other status (preempted, dropped, etc.)
                callbacks.complete?.(); // Ensure flow continues for any other status
            }

            // Clean up after playing
            // audioKit will handle cache eviction automatically via LRU
        } catch (e) {
            dumpLogs({
                action: "direct_play_audio_error",
                sound_url: url,
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
            callbacks.error?.("ERROR", e);
            callbacks.complete?.(); // Ensure flow continues even on exception
        }
    }
    find(name: string) {
        return this.sounds[name];
    }

    // Public method to access audioKit for blessing
    getAudioKit(): AudioKit {
        // Ensure audioKit is properly initialized in browser
        if (typeof window !== "undefined" && !this.audioKit) {
            this.initializeAudioKit();
        }
        return this.audioKit;
    }
    pauseCallback() {
        const audioElement = document.getElementById(
            "interactionSoundComponent",
        ) as HTMLAudioElement;
        const timeout = setTimeout(() => {
            const div = document.getElementById("popup-layout");
            if (
                audioElement.paused &&
                !audioElement.ended &&
                !div &&
                !document.hidden
            ) {
                const sound_name = this.getSoundName(audioElement);
                const playCatch = (e: any) => {
                    if (
                        typeof window != "undefined" &&
                        audioElement.src == window.location.href
                    ) {
                        return;
                    }
                    if (
                        String(e).includes(
                            "The element has no supported sources.",
                        )
                    ) {
                        return;
                    }
                    if (
                        String(e).includes("interact with the document first")
                    ) {
                        dumpLogs({
                            action: "play_audio_no_tap",
                            sound_name,
                            sound: this.sounds[sound_name],
                            pauseSound: this.pauseSound,
                        });
                        this.onError("NO_TAP");
                    } else {
                        dumpLogs({
                            action: "play_audio_play_error_pause_callback",
                            sound_name,
                            sound: this.sounds[sound_name],
                            eventPointer: e,
                            error: e?.message,
                            tError: e?.target?.error,
                            tErrorMsg: e?.target?.error?.message,
                            pauseSound: this.pauseSound,
                        });
                    }
                    captureException(e);
                };
                dumpLogs({
                    action: "play_audio_auto_unpause",
                    sound_url: audioElement.src,
                    sound_name: this.getSoundName(audioElement),
                    pauseSound: this.pauseSound,
                });
                audioElement.play().catch(playCatch);

                this.pauseSound = false;
            }
            clearTimeout(timeout);
        }, 1000);
    }
    pause(name: string = "") {
        const callStack = new Error().stack;

        this.pauseSound = true;

        // Use new pause functionality that preserves position
        const pausedState = this.audioKit.pauseVoice();
        if (pausedState) {
            // Store the paused audio ID for potential resume
            this.pausedAudioId = pausedState.id;
        } else {
            // No active audio to pause, just stop all
            this.audioKit.stopAll();
            if (name) {
                this.pausedAudioId = name;
            }
        }

        dumpLogs({
            action: "play_audio_pause",
            sound_name: name || this.pausedAudioId,
            pauseSound: this.pauseSound,
        });

        this.showPopup();
        this.pauseCallback();
    }
    async resume(name: string = "") {
        const callStack = new Error().stack;

        this.pauseSound = false;

        // Process any queued plays that were interrupted - MUST do this first
        if (this.pendingPlayQueue.length > 0) {
            const queue = [...this.pendingPlayQueue];
            this.pendingPlayQueue = [];

            // Resolve all pending plays so they can continue
            queue.forEach(({ name, resolve }) => {
                resolve();
            });
        }

        try {
            // Clear any cooldowns that might block playback
            this.audioKit.clearCooldowns();

            // Resume audio context if suspended
            await this.audioKit.resume();

            // Try to resume from paused position first
            const resumed = this.audioKit.resumeVoice();

            if (resumed) {
                dumpLogs({
                    action: "play_audio_resumed_from_position",
                    sound_name: this.pausedAudioId,
                    pauseSound: this.pauseSound,
                });
            } else {
                // No paused audio to resume

                dumpLogs({
                    action: "play_audio_resume_no_paused",
                    sound_name: name || this.pausedAudioId,
                    pauseSound: this.pauseSound,
                });

                // Don't try to play anything - the pending play will continue automatically
                // after the promise is resolved above
            }

            this.pausedAudioId = null;
        } catch (e) {
            dumpLogs({
                action: "audio_resume_error",
                error: e,
                pauseSound: this.pauseSound,
            });
            captureException(e);
        }
    }
    stop(name: string) {
        // Since audioKit is in mono mode, we can only stop if it's currently playing
        // We don't have a way to know what's currently playing, so we'll stop all
        dumpLogs({
            action: "play_audio_stop",
            sound_name: name,
            pauseSound: this.pauseSound,
        });

        // Stop all audio (mono mode only has one voice)
        this.audioKit.stopAll();
    }

    stopAll() {
        // Resolve any pending plays to prevent stuck flow
        if (this.pendingPlayQueue.length > 0) {
            this.pendingPlayQueue.forEach(({ name, resolve }) => {
                resolve();
            });
            this.pendingPlayQueue = [];
        }

        dumpLogs({
            action: "play_audio_stop_all",
            pauseSound: this.pauseSound,
        });

        this.audioKit.stopAll();
    }
    forceEnd() {
        // Resolve any pending plays to prevent stuck flow
        if (this.pendingPlayQueue.length > 0) {
            this.pendingPlayQueue.forEach(({ name, resolve }) => {
                resolve();
            });
            this.pendingPlayQueue = [];
        }

        dumpLogs({
            action: "play_audio_force_end",
            pauseSound: this.pauseSound,
        });

        // Stop current audio immediately
        this.audioKit.stopAll();
        this.pauseSound = false;
    }
    removeAll(exceptions: string[] = []) {
        // Get currently playing audio - it should always be preserved
        const currentlyPlaying = this.audioKit.getCurrentlyPlaying();
        if (currentlyPlaying) {
            // Add currently playing to exceptions so it gets reloaded
            if (!exceptions.includes(currentlyPlaying)) {
                exceptions = [...exceptions, currentlyPlaying];
            }
        }

        dumpLogs({
            action: "audio_remove_all",
            sounds: this.sounds,
            pauseSound: this.pauseSound,
        });

        // Store exceptions
        const countDownSound = this.sounds["Count-down-sound"];
        const exceptionAudios = exceptions?.reduce?.((acc: any, name) => {
            if (this.sounds[name]) {
                acc[name] = this.sounds[name];
            }
            return acc;
        }, {});

        // Clear audioKit cache but keep playing current audio
        this.audioKit.clear(true); // true = keep playing

        // Reset sounds tracking but keep exceptions
        this.sounds = {
            "Count-down-sound": countDownSound,
            ...exceptionAudios,
        };
        this.questionAudios = [];

        // Re-preload exception sounds
        const soundsToReload = Object.keys(this.sounds).filter(
            (name) => this.sounds[name],
        );
        if (soundsToReload.length > 0) {
            const items: SoundItem[] = soundsToReload.map((name) => ({
                id: name,
                url: this.sounds[name].url,
            }));
            this.audioKit.preloadCritical(items).catch((e) => {
                dumpLogs({
                    action: "audio_remove_all_reload_error",
                    error: e,
                    sounds: soundsToReload,
                });
            });
        }
    }
    removeOne(name: string) {
        dumpLogs({
            action: "audio_remove_one",
            sounds: this.sounds,
            name,
            pauseSound: this.pauseSound,
        });
        console.log(`[REMOVE ONE]`, name);

        if (this.sounds[name]) {
            delete this.sounds[name];
        }
        this.audioKit.clear(true); // true = keep playing

        // Note: audioKit doesn't have a removeOne method,
        // but the LRU cache will naturally evict it when needed
    }

    addQuestionAudiosList() {
        this.questionAudios =
            FigmaJsonStates.getNonUpdatedJson("question_audios");
    }
    getCurrentPlaying(): {
        name?: string;
        url?: string;
        isPlaying: boolean;
        duration?: number;
        remaining?: number;
    } {
        const audioElement = document.getElementById(
            "interactionSoundComponent",
        ) as HTMLAudioElement | null;

        if (!audioElement || !audioElement.src) {
            return { isPlaying: false };
        }

        const isPlaying = !audioElement.paused && !audioElement.ended;
        const rawDuration = audioElement.duration; // seconds, or NaN
        const duration = Number.isFinite(rawDuration) ? rawDuration : undefined;

        const remaining =
            duration !== undefined
                ? Math.max(0, duration - audioElement.currentTime)
                : undefined;
        const name = this.getSoundName(audioElement);
        if (name) {
            return { name, isPlaying, duration, remaining };
        }
        return { url: audioElement.src, isPlaying, duration, remaining };
    }
}

export const SoundState = new Sound();
